const groupPrefix = createApiRoute(ApiModule.DIETARY, ApiDataModel.MEAL)

/**
 * API functions for diet meals management.
 * @namespace apiMeals
 */
export const apiMeals = {
	/**
	 * Fetches a paginated list of meals.
	 */
	fetchDietMeals: async (
		search: string | undefined,
		type: TDietMealType,
		page: number = 1,
		perPage: number = 25,
		filters?: IApiFilters,
	): Promise<IApiResponseModel<IDietMealModel[]>> => {
		const { data, meta } = await apiCommon<IDietMeal[]>(`${groupPrefix}`, {
			method: 'GET',
			...createQuery({ page, perPage }, { name: search, meal_kind: type, meal_category: 'meal', ...filters }),
		})
		return { data: data.map(d => DietMeal(d)), meta }
	},
	/**
	 * Fetch single meal by ID.
	 */
	fetchDietMeal: async (id: string | number): Promise<IDietMealModel> => {
		const { data } = await apiCommon<IDietMeal>(`${groupPrefix}/${id}`, {
			method: 'GET',
		})
		return DietMeal(data)
	},
	/**
	 * Create new meal
	 */
	createDietMeal: async (body: IDietMealCreatePayload): Promise<IDietMealModel> => {
		const { data } = await apiCommon<IDietMeal>(`${groupPrefix}`, {
			method: 'POST',
			body,
		})
		return DietMeal(data)
	},
	/**
	 * Update existing meal model
	 */
	updateDietMeal: async (body: IDietMealUpdatePayload): Promise<IDietMealModel> => {
		const { data } = await apiCommon<IDietMeal>(`${groupPrefix}/${body.id}`, {
			method: 'PUT',
			body,
		})

		return DietMeal(data)
	},
	/**
	 * Duplicate existing meal model
	 */
	duplicateDietMeal: async (id: number) => {
		const { data } = await apiCommon<IDietMeal>(`${groupPrefix}/${id}/duplicate`, { method: 'POST' })

		return DietMeal(data)
	},
	/**
	 * Delete existing meal model
	 */
	deleteDietMeal: async (id: number) => {
		await apiCommon(`${groupPrefix}/${id}`, { method: 'DELETE' })
	},

	/**
	 * Fetch additional meal variants information.
	 */
	fetchDietMealVariants: async (id: number) => {
		const { data } = await apiCommon<IDietMealVariant[]>(`${groupPrefix}/${id}/variants`, {
			method: 'GET',
		})
		return data.map(d => DietMealVariant(d))
	},
	/**
	 * Fetch meal production steps.
	 */
	fetchDietMealProductionSteps: async (id: number): Promise<IDietMealProductionStepModel[]> => {
		const { data } = await apiCommon<IDietMealsProductionStep[]>(`${groupPrefix}/${id}/production-stages`, {
			method: 'GET',
		})

		return data.map(item => DietProductionStep(item))
	},
	/**
	 * Fetch meal edit history.
	 */
	fetchMealEditHistory: async (id: number, page: number = 1, perPage: number = 25) => {
		const { data, meta } = await apiCommon<IDietMealEditHistory[]>(`${groupPrefix}/${id}/edit-history`, {
			method: 'GET',
			...createQuery({ page, perPage }),
		})
		return { data: data.map(d => DietMealEditHistory(d)), meta }
	},

	/**
	 * @deprecated
	 * Old Function for create new meal
	 */
	createDietMealOld: async (dto: Partial<IDietMealModel>): Promise<IDietMealModel> => {
		const { data } = await apiCommon<IDietMeal>(`${groupPrefix}`, {
			method: 'POST',
			body: dietMealToJSON(dto),
		})
		return DietMeal(data)
	},
	/**
	 * @deprecated
	 * Old Function for edit meal
	 */
	editDietMealsOld: async (dto: Partial<IDietMealModel>, meal: number) => {
		const { data } = await apiCommon<IDietMeal>(`${groupPrefix}/${meal}`, {
			method: 'PUT',
			body: dietMealToJSON(dto),
		})

		return DietMeal(data)
	},
}
